"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { Favicon } from "@/components/icons/favicon";
import type { SectionDef } from "@/lib/constants/sections";
import { preferredScrollBehavior } from "@/lib/scroll-behavior";
import { Button } from "@domainstack/ui/button";
import { ScrollArea } from "@domainstack/ui/scroll-area";
import { cn } from "@domainstack/ui/utils";
interface SectionNavProps {
  domain: string;
  sections: SectionDef[];
  activeSection: string;
  isHeaderVisible: boolean;
  onSectionClick: (slug: string) => void;
}

/**
 * Sticky section navigation bar with "context injection" pattern.
 * When the page header scrolls out of view, the domain name and track button
 * fade into the left side of the nav bar.
 *
 * Sticks below the global header. On mobile it follows the header's `data-scrolled-away`
 * with the same timing: snaps up when the header hides, slides back down with it.
 */
export function SectionNav(t0) {
  const $ = _c(31);
  const {
    domain,
    sections,
    activeSection,
    isHeaderVisible,
    onSectionClick
  } = t0;
  const navRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== activeSection) {
    t1 = () => {
      const viewport = navRef.current?.querySelector("[data-slot=scroll-area-viewport]");
      const activeTab = navRef.current?.querySelector(`[data-section="${activeSection}"]`);
      if (!(viewport instanceof HTMLElement) || !(activeTab instanceof HTMLElement)) {
        return;
      }
      const tabRect = activeTab.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      viewport.scrollTo({
        left: viewport.scrollLeft + tabRect.left - viewportRect.left - (viewportRect.width - tabRect.width) / 2,
        behavior: preferredScrollBehavior()
      });
    };
    t2 = [activeSection];
    $[0] = activeSection;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("sticky top-[var(--header-height)] z-40 -mx-4 mt-4 mb-4 px-4", "transition-[translate] duration-300 ease-out motion-reduce:transition-none md:transition-none", "max-md:[:root:has(header[data-scrolled-away=true])_&]:-translate-y-[var(--header-height)] max-md:[:root:has(header[data-scrolled-away=true])_&]:transition-none");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isHeaderVisible ? "after:bg-gradient-to-r after:from-transparent after:via-black/10 after:to-transparent dark:after:via-white/10" : "shadow-xl shadow-black/5 after:bg-black/10 dark:shadow-none dark:after:bg-white/10";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("relative flex items-center", "h-[var(--section-nav-height)]", "before:absolute before:inset-y-0 before:left-1/2 before:-z-10 before:w-screen before:-translate-x-1/2", "before:bg-background/80 before:backdrop-blur", "after:absolute after:bottom-0 after:left-1/2 after:h-px after:w-screen after:-translate-x-1/2", "after:transition-opacity after:duration-200", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = isHeaderVisible ? "w-0 opacity-0" : "mr-3 w-auto border-r pr-3 opacity-100";
  let t7;
  if ($[6] !== t6) {
    t7 = cn("flex shrink-0 items-center gap-2 overflow-hidden transition duration-200 ease-out", t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = `Scroll to top - ${domain}`;
  let t10;
  let t9;
  if ($[8] !== domain) {
    t9 = <Favicon domain={domain} className="shrink-0" />;
    t10 = <span className="max-w-32 truncate text-[15px] font-medium">{domain}</span>;
    $[8] = domain;
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  let t11;
  if ($[11] !== t10 || $[12] !== t8 || $[13] !== t9) {
    t11 = <button type="button" onClick={_temp} className="flex min-w-0 cursor-pointer items-center gap-2" aria-label={t8}>{t9}{t10}</button>;
    $[11] = t10;
    $[12] = t8;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t7) {
    t12 = <div className={t7}>{t11}</div>;
    $[15] = t11;
    $[16] = t7;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== activeSection || $[19] !== onSectionClick || $[20] !== sections) {
    let t14;
    if ($[22] !== activeSection || $[23] !== onSectionClick) {
      t14 = t15 => {
        const {
          slug,
          title,
          icon: Icon,
          accent
        } = t15;
        return <Button key={slug} data-section={slug} data-accent={accent} variant="ghost" size="sm" onClick={() => onSectionClick(slug)} aria-current={activeSection === slug ? "page" : undefined} style={{
          "--section-accent": `var(--accent-${accent})`
        } as React.CSSProperties} className={cn("rounded-md px-3 py-1.5 text-[13px] tracking-[0.01em] whitespace-nowrap", "transition duration-150", "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none", "hover:!bg-[color-mix(in_oklch,var(--section-accent)_15%,transparent)] hover:text-foreground", activeSection === slug ? "bg-[color-mix(in_oklch,var(--section-accent)_25%,transparent)] font-medium text-accent-foreground" : "text-foreground/75")}><Icon className="size-3.5" />{title}</Button>;
      };
      $[22] = activeSection;
      $[23] = onSectionClick;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    t13 = sections.map(t14);
    $[18] = activeSection;
    $[19] = onSectionClick;
    $[20] = sections;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <ScrollArea className="flex-1 [&_[data-slot=scroll-area-viewport]]:overscroll-y-none" hideScrollbar={true}><div className="flex items-center gap-1 px-1 md:justify-center">{t13}</div></ScrollArea>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t14 || $[29] !== t5) {
    t15 = <nav ref={navRef} aria-label="Section navigation" className={t3}><div className={t5}>{t12}{t14}</div></nav>;
    $[27] = t12;
    $[28] = t14;
    $[29] = t5;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
function _temp() {
  return window.scrollTo({
    top: 0,
    behavior: preferredScrollBehavior()
  });
}